import type { ViewProps } from '../../primitives/View';
import { View } from '../../primitives/View';
import { DARK_LAYER1 } from '../../styles/elevation';
import { cn } from '../../utils/cn';

/**
 * The surface a flashcard (and a quiz question) sits on: 24px radius, the DS
 * `secondary-200` stroke, the `secondary-50` fill (dark: the elevation
 * ladder's content SURFACE step, `styles/elevation.ts` — a card is content, so
 * it sits above the canvas-dark frame), no shadow. On the deck pane, itself a
 * surface, a card adds NO lifted fill in dark: the hairline is its edge, the
 * same as the materials tiles beside it (design review 2026-10, "less layers";
 * a lifted s100 card made every card in the list a fourth level). Lifted from web's
 * `FlashcardBorder`, so the deck's card list, the study card and the quiz
 * question share one stroke on both clients (design review 2026-09:
 * "Flashcards still use custom strokes instead of the design system values" —
 * the phone drew them with a legacy slate border and a navy shadow).
 *
 * Only the frame: padding, size and radius overrides come from the caller
 * (`className`), and the phone's `style` / `onLayout` (`nativeProps`). Web's
 * `FlashcardBorder` keeps its own `<div>` (dozens of layouts lean on it) and
 * composes {@link STUDY_CARD_FRAME_CLASS} instead — same values, one source.
 */
export const STUDY_CARD_FRAME_CLASS = `rounded-3xl border border-secondary-200 bg-secondary-50 ${DARK_LAYER1}`;

export function StudyCardFrame({ className, ...props }: ViewProps) {
  return <View className={cn(STUDY_CARD_FRAME_CLASS, className)} {...props} />;
}
(StudyCardFrame as { displayName?: string }).displayName = 'StudyCardFrame';

/** The 1px line between a card's front and back — the same DS stroke. */
export const STUDY_CARD_DIVIDER_CLASS = 'h-px w-full shrink-0 bg-secondary-200';

export function StudyCardDivider({
  className,
  testID,
}: {
  /** Margins — each surface keeps its own rhythm (`my-3` list, `my-4` study). */
  className?: string;
  testID?: string;
}) {
  return <View className={cn(STUDY_CARD_DIVIDER_CLASS, className)} testID={testID} />;
}
(StudyCardDivider as { displayName?: string }).displayName = 'StudyCardDivider';
